import { RateLimitRule, RateLimitType } from './types';
import { dbRateLimits } from './db';
import { RateLimitError } from '../error';

let allRules: Array<RateLimitRule> = [];

export function initRateLimits(rateLimits: RateLimitRule[]) {
  if (allRules.length > 0) {
    throw new Error('Duplicate call to initRateLimits - already initialized');
  }

  allRules = rateLimits;
}

/**
 * This function will check all rate limit rules on the specified bucket and type,
 * throw an error if any of them are exceeded and increase the count of the rate limit record.
 *
 * @category Rate Limits
 *
 * @example
 * ```ts
 * await consumeRateLimit({ bucket: 'api', type: 'ip', value: '127.0.0.1' });
 * ```
 * @param options.bucket - The bucket for the rate limit.
 * @param options.type - The type of the rate limit.
 * @param options.value - The value for the rate limit.
 * @param options.message - Optional custom error message when the rate limit is exceeded.
 */
export async function consumeRateLimit(options: {
  bucket: string;
  type: RateLimitType;
  value: string;
  message?: string;
}) {
  const { bucket, type, value, message } = options;
  const rules = allRules.filter((rule) => rule.bucket === bucket && rule.type === type);
  const createError = message ? () => new RateLimitError(message) : undefined;

  for (const rule of rules) {
    await checkRateLimitRule(rule, value, createError);
  }
}

// Two-bucket sliding window approximation to track rate limits.
async function checkRateLimitRule(rule: RateLimitRule, value: string, createError?: () => Error) {
  const createRateLimitError = () => {
    return createError
      ? createError()
      : new RateLimitError(`Rate limit exceeded for ${rule.bucket}`);
  };

  const filter = {
    bucket: rule.bucket,
    type: rule.type,
    value,
    windowMs: rule.window,
  };

  const record = await dbRateLimits.findOne(filter);

  const now = Date.now();
  const currentWindowStart = Math.floor(now / rule.window) * rule.window;

  const { count, modifier } = record
    ? getCount(record, currentWindowStart, now)
    : {
        count: 0,
        modifier: {
          $setOnInsert: {
            windowStart: new Date(currentWindowStart),
            windowCount: 1,
            prevWindowCount: 0,
            expiresAt: new Date(currentWindowStart + rule.window + rule.window),
          },
        },
      };

  if (count >= rule.limit) {
    throw createRateLimitError();
  }

  /*
    Always use upsert, because there is a small chance the document might be auto-removed
    based on the expiration TTL index in between the check and the update
  */
  await dbRateLimits.upsertOne(filter, modifier);
}

function getCount(record: (typeof dbRateLimits)['Doc'], currentWindowStart: number, now: number) {
  const prevWindowStart = currentWindowStart - record.windowMs;

  if (record.windowStart.getTime() === currentWindowStart) {
    const currentWindowCount = record.windowCount;
    const prevWindowCount = record.prevWindowCount;
    const prevWindowWeight = 1 - (now - currentWindowStart) / record.windowMs;
    return {
      count: Math.round(currentWindowCount + prevWindowCount * prevWindowWeight),
      modifier: {
        $inc: { windowCount: 1 },
        $setOnInsert: {
          windowStart: new Date(currentWindowStart),
          prevWindowCount: 0,
          expiresAt: new Date(currentWindowStart + record.windowMs + record.windowMs),
        },
      },
    };
  }

  if (record.windowStart.getTime() === prevWindowStart) {
    const weight = 1 - (now - currentWindowStart) / record.windowMs;
    return {
      count: Math.round(record.windowCount * weight),
      modifier: {
        $set: {
          windowStart: new Date(currentWindowStart),
          windowCount: 1,
          prevWindowCount: record.windowCount,
          expiresAt: new Date(currentWindowStart + record.windowMs + record.windowMs),
        },
      },
    };
  }

  return {
    count: 0,
    modifier: {
      $set: {
        windowStart: new Date(currentWindowStart),
        windowCount: 1,
        prevWindowCount: 0,
        expiresAt: new Date(currentWindowStart + record.windowMs + record.windowMs),
      },
    },
  };
}
